import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import TooltipContext from "../context/TooltipContext.js";
import DataContext from "../context/DataContext.js";
import getScaleBandwidth from "../utils/getScaleBandwidth.js";
import isValidNumber from "../typeguards/isValidNumber.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/** fontSize + lineHeight from default styles break precise location of crosshair, etc. */
const TOOLTIP_NO_STYLE = {
  position: 'absolute',
  pointerEvents: 'none',
  fontSize: 0,
  lineHeight: 0
};
const INVISIBLE_STYLES = {
  position: 'absolute',
  left: 0,
  top: 0,
  opacity: 0,
  width: 0,
  height: 0,
  pointerEvents: 'none'
};
function DefaultGlyph(_ref) {
  let {
    x,
    y,
    size,
    color,
    glyphStyle
  } = _ref;
  const {
    theme
  } = useContext(DataContext) || {};
  return /*#__PURE__*/_jsx("circle", {
    cx: x,
    cy: y,
    r: size,
    fill: color,
    stroke: theme?.backgroundColor,
    strokeWidth: 1.5,
    paintOrder: "fill",
    ...glyphStyle
  });
}
function defaultRenderGlyph(_ref2) {
  let {
    key,
    ...props
  } = _ref2;
  return /*#__PURE__*/_jsx(DefaultGlyph, {
    ...props
  });
}
function TooltipInner(_ref3) {
  let {
    debounce,
    detectBounds,
    horizontalCrosshairStyle,
    glyphStyle,
    renderTooltip,
    renderGlyph = defaultRenderGlyph,
    resizeObserverPolyfill: resizeObserverPolyfillProp,
    scroll = true,
    showDatumGlyph = false,
    showHorizontalCrosshair = false,
    showSeriesGlyphs = false,
    showVerticalCrosshair = false,
    snapTooltipToDatumX = false,
    snapTooltipToDatumY = false,
    verticalCrosshairStyle,
    zIndex,
    ...tooltipProps
  } = _ref3;
  const {
    colorScale,
    theme,
    innerHeight,
    innerWidth,
    margin,
    xScale,
    yScale,
    dataRegistry,
    resizeObserverPolyfill
  } = useContext(DataContext) || {};
  const tooltipContext = useContext(TooltipContext);
  const {
    containerRef,
    TooltipInPortal,
    forceRefreshBounds
  } = useTooltipInPortal({
    debounce,
    detectBounds,
    polyfill: resizeObserverPolyfill || resizeObserverPolyfillProp,
    scroll,
    zIndex
  });

  // To correctly position itself in a Portal, the tooltip must know its container bounds
  // this is done by rendering an invisible node whose ref can be used to find its parentElement
  const setContainerRef = useCallback(ownRef => {
    containerRef(ownRef?.parentElement ?? null);
  }, [containerRef]);
  const tooltipContent = tooltipContext?.tooltipOpen ? renderTooltip({
    ...tooltipContext,
    colorScale
  }) : null;
  const showTooltip = tooltipContext?.tooltipOpen && tooltipContent != null;

  // useTooltipInPortal is powered by react-use-measure and will update portal positions upon
  // resize and page scroll. however it **cannot** detect when a chart container moves on a
  // page due to animation or drag-and-drop, etc.
  // therefore we force refresh the bounds any time we transition from a hidden tooltip to
  // one that is visible.
  const lastShowTooltip = useRef(false);
  useEffect(() => {
    if (showTooltip && !lastShowTooltip.current) {
      forceRefreshBounds();
    }
    lastShowTooltip.current = showTooltip;
  }, [showTooltip, forceRefreshBounds]);
  let tooltipLeft = tooltipContext?.tooltipLeft;
  let tooltipTop = tooltipContext?.tooltipTop;
  const xScaleBandwidth = xScale ? getScaleBandwidth(xScale) : 0;
  const yScaleBandwidth = yScale ? getScaleBandwidth(yScale) : 0;
  const getDatumLeftTop = useCallback((key, datum) => {
    const entry = dataRegistry?.get(key);
    const xAccessor = entry?.xAccessor;
    const yAccessor = entry?.yAccessor;
    const left = xScale && xAccessor ? Number(xScale(xAccessor(datum))) + xScaleBandwidth / 2 : undefined;
    const top = yScale && yAccessor ? Number(yScale(yAccessor(datum))) + yScaleBandwidth / 2 : undefined;
    return {
      left,
      top
    };
  }, [dataRegistry, xScaleBandwidth, yScaleBandwidth, xScale, yScale]);
  const nearestDatum = tooltipContext?.tooltipData?.nearestDatum;
  const nearestDatumKey = nearestDatum?.key ?? '';

  // snap x- or y-coord to the actual data point (not event coordinates)
  if (showTooltip && nearestDatum && (snapTooltipToDatumX || snapTooltipToDatumY)) {
    const {
      left,
      top
    } = getDatumLeftTop(nearestDatumKey, nearestDatum.datum);
    tooltipLeft = snapTooltipToDatumX && isValidNumber(left) ? left : tooltipLeft;
    tooltipTop = snapTooltipToDatumY && isValidNumber(top) ? top : tooltipTop;
  }

  // collect positions + styles for glyphs; glyphs always snap to Datum, not event coords
  const glyphProps = [];
  if (showTooltip && (showDatumGlyph || showSeriesGlyphs)) {
    const size = Number(glyphStyle?.radius ?? 4);
    if (showSeriesGlyphs) {
      Object.values(tooltipContext?.tooltipData?.datumByKey ?? {}).forEach(_ref4 => {
        let {
          key,
          datum,
          index
        } = _ref4;
        const color = colorScale?.(key) ?? theme?.htmlLabel?.color ?? '#222';
        const {
          left,
          top
        } = getDatumLeftTop(key, datum);

        // don't show glyphs if coords are unavailable
        if (!isValidNumber(left) || !isValidNumber(top)) return;
        glyphProps.push({
          key,
          color,
          datum,
          index,
          size,
          x: left,
          y: top,
          glyphStyle,
          isNearestDatum: nearestDatum ? nearestDatum.key === key : false
        });
      });
    } else if (nearestDatum) {
      const {
        left,
        top
      } = getDatumLeftTop(nearestDatumKey, nearestDatum.datum);
      // don't show glyphs if coords are unavailable
      if (isValidNumber(left) && isValidNumber(top)) {
        const color = (nearestDatumKey && colorScale?.(nearestDatumKey)) ??
        // @ts-expect-error
        null ?? theme?.gridStyles?.stroke ?? theme?.htmlLabel?.color ?? '#222';
        glyphProps.push({
          key: nearestDatumKey,
          color,
          datum: nearestDatum.datum,
          index: nearestDatum.index,
          size,
          x: left,
          y: top,
          glyphStyle,
          isNearestDatum: true
        });
      }
    }
  }
  return (
    /*#__PURE__*/
    // Tooltip can be rendered as a child of SVG or HTML since its output is rendered in a Portal.
    // So use svg element to find container ref because it's a valid child of SVG and HTML parents.
    _jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("svg", {
        ref: setContainerRef,
        style: INVISIBLE_STYLES
      }), showTooltip && /*#__PURE__*/_jsxs(_Fragment, {
        children: [showVerticalCrosshair && /*#__PURE__*/_jsx(TooltipInPortal, {
          className: "visx-crosshair visx-crosshair-vertical",
          left: tooltipLeft,
          top: margin?.top,
          offsetLeft: 0,
          offsetTop: 0,
          detectBounds: false,
          style: TOOLTIP_NO_STYLE,
          children: /*#__PURE__*/_jsx("svg", {
            width: "1",
            height: innerHeight,
            overflow: "visible",
            children: /*#__PURE__*/_jsx("line", {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: innerHeight,
              strokeWidth: 1.5,
              stroke: theme?.gridStyles?.stroke ?? theme?.htmlLabel?.color ?? '#222',
              ...verticalCrosshairStyle
            })
          })
        }), showHorizontalCrosshair && /*#__PURE__*/_jsx(TooltipInPortal, {
          className: "visx-crosshair visx-crosshair-horizontal",
          left: margin?.left,
          top: tooltipTop,
          offsetLeft: 0,
          offsetTop: 0,
          detectBounds: false,
          style: TOOLTIP_NO_STYLE,
          children: /*#__PURE__*/_jsx("svg", {
            width: innerWidth,
            height: "1",
            overflow: "visible",
            children: /*#__PURE__*/_jsx("line", {
              x1: 0,
              x2: innerWidth,
              y1: 0,
              y2: 0,
              strokeWidth: 1.5,
              stroke: theme?.gridStyles?.stroke ?? theme?.htmlLabel?.color ?? '#222',
              ...horizontalCrosshairStyle
            })
          })
        }), glyphProps.map(_ref5 => {
          let {
            key,
            x,
            y,
            ...props
          } = _ref5;
          return (
            /*#__PURE__*/
            // We render glyps in a portal so that they can overflow the container if necessary
            _jsx(TooltipInPortal, {
              className: "visx-tooltip-glyph",
              left: x,
              top: y,
              offsetLeft: 0,
              offsetTop: 0,
              detectBounds: false,
              style: TOOLTIP_NO_STYLE,
              children: /*#__PURE__*/_jsx("svg", {
                overflow: "visible",
                children: renderGlyph({
                  key,
                  x: 0,
                  y: 0,
                  ...props
                })
              })
            }, key)
          );
        }), /*#__PURE__*/_jsx(TooltipInPortal, {
          left: tooltipLeft,
          top: tooltipTop,
          style: {
            ...defaultStyles,
            background: theme?.backgroundColor ?? 'white',
            boxShadow: `0 1px 2px 
                ${theme?.htmlLabel?.color ? `${theme?.htmlLabel?.color}55` : '#22222255'}`,
            ...theme?.htmlLabel
          },
          ...tooltipProps,
          children: tooltipContent
        })]
      })]
    })
  );
}

/**
 * This is a wrapper component which bails early if tooltip is not visible.
 * If scroll detection is enabled in UseTooltipPortalOptions, this avoids re-rendering
 * the component on every scroll. If many charts with Tooltips are rendered on a page,
 * this also avoids creating many resize observers / hitting browser limits.
 */
export default function Tooltip(props) {
  const tooltipContext = useContext(TooltipContext);
  if (!tooltipContext?.tooltipOpen) return null;
  return /*#__PURE__*/_jsx(TooltipInner, {
    ...props
  });
}